import type { ResourceRate } from '../../../services/vendorService';
import { currency, shortDate } from './format';

/**
 * M19 - which rate is in effect today, and which are still to come.
 *
 * Rate windows are whole days, both ends inclusive, stored at 00:00Z (see
 * server/src/modules/vendor/rate-window.ts). Comparing the yyyy-mm-dd part against today's
 * calendar date keeps a rate that ends today "current" all day, and keeps a rate starting
 * tomorrow "upcoming" until then - however far the viewer is from UTC.
 *
 * SUPERSEDED counts: approving a future change marks the outgoing rate SUPERSEDED at once, but
 * it stays the price in effect until the day before the new one starts.
 */

const dayKey = (iso: string | null | undefined): string | null => (iso ? iso.slice(0, 10) : null);

export function todayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const LIVE = new Set(['APPROVED', 'SUPERSEDED']);

export function isRateCurrent(rate: Pick<ResourceRate, 'status' | 'effectiveFrom' | 'effectiveTo'>, today = todayKey()): boolean {
  if (!LIVE.has(rate.status)) return false;
  const from = dayKey(rate.effectiveFrom);
  const to = dayKey(rate.effectiveTo);
  return !!from && from <= today && (to === null || to >= today);
}

export function isRateUpcoming(rate: Pick<ResourceRate, 'status' | 'effectiveFrom'>, today = todayKey()): boolean {
  const from = dayKey(rate.effectiveFrom);
  return rate.status === 'APPROVED' && !!from && from > today;
}

export interface AreaRateSummary {
  serviceAreaId: number;
  areaLabel: string;
  current: ResourceRate | null;
  upcoming: ResourceRate[];
  pending: ResourceRate[];
}

/** One entry per service area, in first-seen order. */
export function summariseRatesByArea(rates: ResourceRate[], today = todayKey()): AreaRateSummary[] {
  const byArea = new Map<number, AreaRateSummary>();
  for (const r of rates) {
    let entry = byArea.get(r.serviceAreaId);
    if (!entry) {
      entry = {
        serviceAreaId: r.serviceAreaId,
        areaLabel: r.serviceArea?.code || r.serviceArea?.name || String(r.serviceAreaId),
        current: null,
        upcoming: [],
        pending: [],
      };
      byArea.set(r.serviceAreaId, entry);
    }
    if (r.status === 'PENDING') entry.pending.push(r);
    else if (isRateCurrent(r, today)) {
      // Latest start wins if windows ever overlap, matching resolveRate on the server.
      if (!entry.current || r.effectiveFrom > entry.current.effectiveFrom) entry.current = r;
    } else if (isRateUpcoming(r, today)) entry.upcoming.push(r);
  }
  for (const entry of byArea.values()) {
    entry.upcoming.sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  }
  return [...byArea.values()];
}

/** "$175.00 from Jan 1, 2027" */
export const upcomingLabel = (r: Pick<ResourceRate, 'rate' | 'effectiveFrom'>): string =>
  `${currency(r.rate)} from ${shortDate(r.effectiveFrom)}`;

/** Display status for one rate row in the rate history (RateModal). */
export function rateRowStatus(
  r: Pick<ResourceRate, 'status' | 'effectiveFrom' | 'effectiveTo'>,
  today = todayKey()
): 'current' | 'upcoming' | 'pending' | 'ended' | 'rejected' {
  if (r.status === 'PENDING') return 'pending';
  if (r.status === 'REJECTED') return 'rejected';
  if (isRateCurrent(r, today)) return 'current';
  if (isRateUpcoming(r, today)) return 'upcoming';
  return 'ended';
}

/**
 * V2-007: a resource whose only rate is a first rate still awaiting approval is not usable yet.
 * It shows as Pending and is not offered on hired equipment lines until that rate is approved.
 * Bundles price from their components, so they are never held back this way.
 */
export function isAwaitingFirstRate(resource: {
  isActive: boolean;
  resourceType: string;
  rates?: Array<Pick<ResourceRate, 'status'>>;
}): boolean {
  if (!resource.isActive || resource.resourceType === 'BUNDLE') return false;
  const rates = resource.rates ?? [];
  const hasLiveRate = rates.some((r) => r.status === 'APPROVED' || r.status === 'SUPERSEDED');
  return !hasLiveRate && rates.some((r) => r.status === 'PENDING');
}
